import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Cloudflare Turnstile widget (Phase 3 P0 anti-bot).
 * Explicit render — safer for SPA/HMR than the auto-inject mode.
 *
 * Loads the CF script once, waits for `turnstile.ready()`, then renders
 * into our container. Emits token (or null on error/expiry) via onToken.
 *
 * Site key comes from REACT_APP_TURNSTILE_SITE_KEY. When the key is not
 * set locally we render nothing and immediately emit a `null` token so
 * the parent's "gate submit until token exists" logic keeps working —
 * dev without a site key just skips the widget (backend also no-ops in
 * that case).
 */
let scriptPromise;
function loadTurnstile() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    // NB: Cloudflare's api.js throws "Remove async/defer from the Turnstile
    // api.js script tag before using turnstile.ready()" when either flag
    // is set. Dynamically-created scripts default to async=true — we must
    // explicitly opt out. See CF docs: client-side-rendering → Explicit.
    s.async = false;
    s.defer = false;
    s.crossOrigin = 'anonymous';
    s.onload = () => {
      if (window.turnstile) window.turnstile.ready(resolve);
      else reject(new Error('turnstile object missing after load'));
    };
    s.onerror = () => reject(new Error('turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// One-time global filter: swallow "Script error." events that originate
// from the CF Turnstile CDN so the CRA dev overlay doesn't paint over the
// entire signup form when Cloudflare rejects an unauthorized hostname.
// The user still sees our inline error message via `onError`.
let _installedGlobalFilter = false;
function installGlobalErrorFilter() {
  if (_installedGlobalFilter || typeof window === 'undefined') return;
  _installedGlobalFilter = true;
  window.addEventListener('error', (ev) => {
    const src = ev?.filename || '';
    if (src && src.includes('challenges.cloudflare.com')) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }
  }, true);
  window.addEventListener('unhandledrejection', (ev) => {
    const msg = String(ev?.reason?.message || ev?.reason || '');
    if (msg.toLowerCase().includes('turnstile')) {
      ev.preventDefault();
    }
  });
}

export default function TurnstileWidget({ onToken, onError, action = 'signup', theme = 'dark' }) {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const emitError = useCallback((msg) => {
    onToken?.(null);
    onError?.(msg);
  }, [onToken, onError]);

  useEffect(() => {
    installGlobalErrorFilter();
    if (!siteKey) {
      // No site key configured — silently skip. The backend also skips
      // verification in this case (dev-only path).
      onToken?.('__no_turnstile__');
      return () => {};
    }
    let cancelled = false;
    loadTurnstile().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      try {
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme,
          callback: (token) => onToken?.(token),
          'error-callback': () => emitError('Security check failed. Please retry.'),
          'expired-callback': () => emitError('Security check expired. Please complete it again.'),
          'timeout-callback': () => emitError('Security check timed out. Please retry.'),
        });
      } catch (renderErr) {
        emitError('Security check could not render on this domain.');
      }
    }).catch(() => emitError('Security check could not load. Refresh the page and try again.'));

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch (_) { /* noop */ }
      }
      widgetId.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, action, theme]);

  if (!siteKey) return null;
  return (
    <div
      ref={containerRef}
      data-testid="turnstile-widget"
      style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 8px' }}
      aria-label="Cloudflare bot protection"
    />
  );
}
